import _ from 'lodash';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchForTable, tableChange } from '../../actions/table.actions';
import OfferingsFilter from '../../components/offerings/OfferingsFilter';
import OfferingsList from '../../components/offerings/OfferingsList';
import { OFFERINGS_DETAIL_PATH } from '../../constants/paths.constants';
import { usePrevious } from '../../hooks/usePrevious';
import { OFFERINGS_RESOURCE } from '../../resources/offerings.resource';
import { fetchErrorSelector, fetchingSelector, shouldFetchSelector } from '../../selectors/fetch.selectors';
import { tableDataSelector } from '../../selectors/resources.selectors';
import { offerings } from '../../services/bff.api';

const resource = { ...OFFERINGS_RESOURCE, api: { get: offerings.searchRestricted } };

const Offerings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const prevSearch = usePrevious(location.search);
  const [_searchParams, setSearchParams] = useSearchParams();
  const shouldFetch = useSelector((state) => shouldFetchSelector(state, { resource }));
  const isLoading = useSelector((state) => fetchingSelector(state, { resource }));
  const fetchError = useSelector((state) => fetchErrorSelector(state, { resource }));
  const { data, config } = useSelector((state) => tableDataSelector(state, { resource }));
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [search, setSearch] = useState('');
  const themes = Array.isArray(queryString.parse(location.search, { arrayFormat: 'comma' }).themes)
    ? queryString.parse(location.search, { arrayFormat: 'comma' }).themes
    : [queryString.parse(location.search, { arrayFormat: 'comma' }).themes].filter((x) => !!x);

  const handleChange = (change) => (isLoading ? null : dispatch(tableChange(resource, change)));

  const handleSelect = (offering) => {
    navigate(generatePath(OFFERINGS_DETAIL_PATH, { offeringId: offering.id }));
  };

  const handleFilterSubmit = () => {
    const newSearch = {};
    if (search) {
      newSearch.content = search;
    }
    if (themes.length > 0) {
      newSearch.themes = themes;
    }
    setSearchParams(queryString.stringify(newSearch, { arrayFormat: 'comma' }), { replace: true });
  };

  const handleThemeChange = (theme) => {
    if (theme === '' && themes.length === 0) {
      return;
    }
    const newSearch = {};
    if (search) {
      newSearch.content = search;
    }
    if (theme !== '') {
      newSearch.themes = themes.includes(theme) ? themes.filter((t) => t !== theme) : [...themes, theme];
    }
    if (theme === '') {
      newSearch.themes = [];
    }
    setSearchParams(queryString.stringify(newSearch, { arrayFormat: 'comma' }), { replace: true });
  };

  useEffect(
    () => filtersInitialized && shouldFetch && dispatch(fetchForTable(resource)),
    [shouldFetch, filtersInitialized, dispatch]
  );

  useEffect(() => {
    if (!filtersInitialized) {
      const currentSearch = queryString.parse(location.search, { arrayFormat: 'comma' });
      setSearch(currentSearch.content || '');
      dispatch(tableChange(OFFERINGS_RESOURCE, { ...config, ..._.pick(currentSearch, ['content', 'themes']) }));
      setFiltersInitialized(true);
    }
  }, [dispatch, config, filtersInitialized, location.search]);

  useEffect(() => {
    if (filtersInitialized && prevSearch !== location.search) {
      const currentSearch = queryString.parse(location.search, { arrayFormat: 'comma' });
      dispatch(tableChange(OFFERINGS_RESOURCE, { ...config, ..._.pick(currentSearch, ['content', 'themes']) }));
    }
  }, [dispatch, config, filtersInitialized, prevSearch, location.search]);

  return (
    <div className="spo-p-offerings u-margin-bottom-md">
      <div className="spo-p-offerings__header"></div>
      <h1 className="u-padding u-bg-light">Doe je aanvraag</h1>
      <div className="row u-padding">
        <div className="col-xs-12 col-md-4 col-xl-3 spo-p-offerings__filters">
          <OfferingsFilter
            search={search}
            themes={themes}
            onSearchChange={setSearch}
            onSearchSubmit={handleFilterSubmit}
            onThemeChange={handleThemeChange}
            isLoading={isLoading}
          />
        </div>
        <div className="col-xs-12 col-md-8 col-xl-9 spo-p-offerings__content">
          <OfferingsList
            items={data?.map((i) => ({ ...i, id: i.id.toString() }))}
            pagination={config}
            isLoading={isLoading}
            error={fetchError}
            onChange={handleChange}
            onSelect={handleSelect}
          />
        </div>
      </div>
    </div>
  );
};

export default Offerings;
